import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CopyIcon,
  EyeOffIcon,
  LetterTextIcon,
  PlusIcon,
  SquareChevronDownIcon,
  Trash2Icon,
} from 'lucide-react'

import type { CommandGroup, CommandMap } from '@/shared/commands/command.types'

import { ColumnType } from '~shared/rundown/column'
import type { Column } from '~shared/rundown/column'

import { useColumnSnapshot } from '../data/columns.store'

import { useColumnAccess } from './column-access'
import { columnLayoutActions } from './column-layout'
import { columnActions, useIsAddingColumn } from './column.actions'
import { useColumnsDerived } from './column.derived'

export type ColumnCommandContext = {
  columnId: Column['id']
  columnType: ColumnType
  isFirst: boolean
  isLast: boolean
  isOwnPrivate: boolean
  isEditor: boolean
  canConfigure: boolean
  canWriteColumn: boolean
  isAddingColumn: boolean
}

/**
 * Command context for the column with this id.
 *
 * @returns null while the column document is missing
 */
export function useColumnCommandContext(
  columnId: Column['id'],
): ColumnCommandContext | null {
  const column = useColumnSnapshot(columnId)
  const access = useColumnAccess({
    id: columnId,
    privateUid: column?.privateUid ?? null,
  })
  const { visibleColumns } = useColumnsDerived()
  const isAddingColumn = useIsAddingColumn()

  if (!column) return null

  const index = visibleColumns.findIndex((c) => c.id === columnId)

  return {
    columnId,
    columnType: column.type,
    isFirst: index <= 0,
    isLast: index === visibleColumns.length - 1,
    isOwnPrivate: access.isOwnPrivate,
    isEditor: access.isEditor,
    canConfigure: access.canConfigure,
    canWriteColumn: access.canWriteColumn,
    isAddingColumn,
  }
}

export const columnCommands = {
  moveLeft: {
    id: 'column.moveLeft',
    label: 'Move left',
    icon: ArrowLeftIcon,
    keywords: ['reorder'],
    isEnabled: ({ isFirst }) => !isFirst,
    execute: ({ columnId }) => columnActions.moveByDirection(columnId, 'left'),
  },

  moveRight: {
    id: 'column.moveRight',
    label: 'Move right',
    icon: ArrowRightIcon,
    keywords: ['reorder'],
    isEnabled: ({ isLast }) => !isLast,
    execute: ({ columnId }) => columnActions.moveByDirection(columnId, 'right'),
  },

  addAfter: {
    id: 'column.addAfter',
    label: 'Add column after',
    icon: PlusIcon,
    keywords: ['new', 'create'],
    isEnabled: ({ isEditor, isAddingColumn }) => isEditor && !isAddingColumn,
    execute: ({ columnId }) => void columnActions.add(columnId),
  },

  hide: {
    id: 'column.hide',
    label: 'Hide column for me',
    icon: EyeOffIcon,
    keywords: ['visibility'],
    isEnabled: ({ isOwnPrivate }) => !isOwnPrivate,
    execute: ({ columnId }) => columnLayoutActions.toggleHidden(columnId),
  },

  copyId: {
    id: 'column.copyId',
    label: 'Copy column ID',
    icon: CopyIcon,
    keywords: ['clipboard'],
    isEnabled: ({ isEditor }) => isEditor,
    execute: ({ columnId }) => columnActions.copyId(columnId),
  },

  convertToDropdown: {
    id: 'column.convertToDropdown',
    label: 'Convert to Dropdown',
    icon: SquareChevronDownIcon,
    keywords: ['column type', 'change type', 'select'],
    isEnabled: ({ canConfigure }) => canConfigure,
    isVisible: ({ columnType }) => columnType !== ColumnType.SELECT,
    execute: ({ columnId }) =>
      void columnActions.convert(columnId, ColumnType.SELECT),
  },

  convertToRichtext: {
    id: 'column.convertToRichtext',
    label: 'Convert to Richtext',
    icon: LetterTextIcon,
    keywords: ['column type', 'change type', 'text'],
    isEnabled: ({ canConfigure }) => canConfigure,
    isVisible: ({ columnType }) => columnType === ColumnType.SELECT,
    execute: ({ columnId }) =>
      void columnActions.convert(columnId, ColumnType.RICHTEXT),
  },

  delete: {
    id: 'column.delete',
    variant: 'destructive',
    label: 'Delete column',
    icon: Trash2Icon,
    keywords: ['remove', 'trash'],
    isEnabled: ({ canWriteColumn }) => canWriteColumn,
    isVisible: ({ isOwnPrivate }) => !isOwnPrivate,
    execute: ({ columnId }) => void columnActions.delete(columnId),
  },

  clear: {
    id: 'column.clear',
    variant: 'destructive',
    label: 'Clear column',
    icon: Trash2Icon,
    keywords: ['remove', 'private'],
    isEnabled: ({ canWriteColumn }) => canWriteColumn,
    isVisible: ({ isOwnPrivate }) => isOwnPrivate,
    execute: ({ columnId }) => void columnActions.delete(columnId),
  },
} satisfies CommandMap<ColumnCommandContext>

/** Shared by the header dropdown and the right-click menu. */
export const columnMenuGroups: CommandGroup<ColumnCommandContext>[] = [
  {
    commands: [columnCommands.hide],
  },
  {
    commands: [
      columnCommands.moveLeft,
      columnCommands.moveRight,
      columnCommands.addAfter,
    ],
  },
  {
    commands: [
      columnCommands.convertToDropdown,
      columnCommands.convertToRichtext,
    ],
  },
  {
    commands: [columnCommands.copyId],
  },
  {
    commands: [columnCommands.delete, columnCommands.clear],
  },
]
